import React from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';

const ExecutionPanel = ({ results, onClose }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-900';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Résultats d'Exécution</h2>
            <p className="text-sm text-gray-600 mt-1">
              Exécuté le {new Date(results.execution_date || Date.now()).toLocaleString('fr-FR')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{results.total_nodes || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Nœuds totaux</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{results.successful_nodes || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Réussis</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{results.failed_nodes || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Échoués</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{results.execution_time || '0'}s</div>
              <div className="text-sm text-gray-600 mt-1">Durée</div>
            </div>
          </div>
        </div>

        {/* Node Execution Details */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="font-bold text-gray-900 mb-4">Détails par Nœud</h3>
          <div className="space-y-3">
            {results.node_results && results.node_results.length > 0 ? (
              results.node_results.map((nodeResult, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 ${getStatusColor(nodeResult.status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getStatusIcon(nodeResult.status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{nodeResult.node_label || 'Nœud'}</span>
                          <span className="text-xs px-2 py-0.5 bg-white bg-opacity-50 rounded">
                            {nodeResult.node_id}
                          </span>
                        </div>
                        {nodeResult.message && (
                          <p className="text-sm mb-2">{nodeResult.message}</p>
                        )}
                        {nodeResult.output && (
                          <div className="mt-2 p-3 bg-white bg-opacity-70 rounded border border-current border-opacity-20">
                            <div className="text-xs font-semibold mb-1">Sortie :</div>
                            <pre className="text-xs whitespace-pre-wrap break-words">
                              {typeof nodeResult.output === 'string'
                                ? nodeResult.output
                                : JSON.stringify(nodeResult.output, null, 2)}
                            </pre>
                          </div>
                        )}
                        {nodeResult.error && (
                          <div className="mt-2 p-3 bg-red-100 rounded border border-red-300">
                            <div className="text-xs font-semibold mb-1 text-red-900">Erreur :</div>
                            <pre className="text-xs whitespace-pre-wrap break-words text-red-800">
                              {nodeResult.error}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                    {nodeResult.execution_time && (
                      <div className="text-xs text-gray-600 ml-4">
                        {nodeResult.execution_time}ms
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>Aucun détail d'exécution disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {results.status === 'completed' ? (
                <span className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  Workflow complété avec succès
                </span>
              ) : results.status === 'failed' ? (
                <span className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-4 h-4" />
                  Workflow échoué
                </span>
              ) : (
                <span className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-4 h-4" />
                  Workflow terminé avec des avertissements
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutionPanel;
